/**
 * The festival's logo, with the edition's name as the fallback.
 *
 * `logoUrl` has been in the shared `EditionConfig` contract since the bundle
 * was written, but the API never populated it — so every festival rendered its
 * NAME as text in the app bar, and MyRave looked like a directory listing
 * rather than like the festival. The backend fills it in now; this renders it.
 *
 * The fallback is not a placeholder to be removed later. Most festivals will
 * upload a logo and some will not, and a wordmark set in the app's own type is
 * a better answer for the second group than an empty bar or a broken image.
 */
import { useEffect, useState, type ImgHTMLAttributes } from 'react';
import { useEdition } from '@/app/EditionProvider';
import { useDarkLogo } from '@/lib/logoTone';
import './Logo.css';

/**
 * SVG logos are painted as a MASK, not as an image, and that is deliberate.
 *
 * The pilot's logo is a single path set declaring `fill="currentColor"` — it is
 * drawn to be tinted by whatever hosts it. Inside an `<img>` that colour never
 * arrives: a standalone SVG resolves `currentColor` against its own root, which
 * defaults to black, so the logo renders black on a near-black app bar and
 * disappears. A mask takes the shape and fills it with the element's colour,
 * which is what the pilot's inline `<svg>` achieved.
 *
 * The trade-off, stated plainly: a MULTICOLOUR svg is flattened to one colour
 * by this. That is the right default for an app-bar wordmark and the wrong one
 * for full-colour artwork — a festival with the latter should upload a PNG or
 * WebP, which takes the `<img>` path below untouched.
 */
export const isSvg = (url: string) => /\.svg(\?|#|$)/i.test(url);

/**
 * Every picture logo in the app — a rave's or a sponsor's. A logo drawn dark
 * for a white page is shown lightened on this dark one (`lib/logoTone.ts`,
 * the user 2026-10-27: "Tomorrowland is not visible" … "fixed for everything").
 */
export function LogoImg({ className, ...img }: ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const dark = useDarkLogo(img.src);
  return <img {...img} className={[className, dark && 'logo-lift'].filter(Boolean).join(' ') || undefined} />;
}

export function Logo({ height = 24, title }: { height?: number; title?: string }) {
  const { bundle } = useEdition();
  const [failed, setFailed] = useState(false);
  const src = bundle.edition.logoUrl;
  const name = bundle.edition.name;

  if (!src || failed) {
    return (
      <span
        title={title}
        className="logo-wordmark"
        style={{ fontSize: Math.round(height * 0.66) }}
      >
        {name}
      </span>
    );
  }

  if (isSvg(src)) {
    return (
      <span
        role="img"
        aria-label={name}
        title={title}
        className="logo-mask"
        style={{
          height,
          /* The pilot's mark is 283.3 × 56.82, so the box follows its ratio. */
          width: height * (283.3 / 56.82),
          WebkitMaskImage: `url("${src}")`,
          maskImage: `url("${src}")`,
        }}
      />
    );
  }

  return (
    <LogoImg
      src={src}
      alt={name}
      title={title}
      onError={() => setFailed(true)}
      className="logo-image"
      style={{ height }}
    />
  );
}

/**
 * A rave's logo from its URL alone — for screens outside a rave (the admins,
 * My raves), which have no `useEdition()`. Same rule as `Logo`: an SVG is a
 * MASK in the text colour, so the pilot's `currentColor` wordmark is not
 * drawn black on a dark header (the user, 2026-10-10: "why is the OZORA
 * logo black?"); anything else is an `<img>`. The mask's width follows the
 * file's own ratio once it has loaded, the pilot's until then.
 */
export function LogoArt({ src, alt = '', height: wanted, title, className, fitWidth }: {
  src: string; alt?: string; height: number; title?: string; className?: string;
  /** Draw it this wide instead (the phone hero, 2026-10-15) — never taller than `height`. */
  fitWidth?: number;
}) {
  const [ratio, setRatio] = useState(283.3 / 56.82);
  const svg = isSvg(src) || src.startsWith('data:image/svg');
  useEffect(() => {
    if (!svg && !fitWidth) return undefined;
    let live = true;
    const probe = new Image();
    probe.onload = () => { if (live && probe.naturalWidth && probe.naturalHeight) setRatio(probe.naturalWidth / probe.naturalHeight); };
    probe.src = src;
    return () => { live = false; };
  }, [src, svg, fitWidth]);
  const height = fitWidth ? Math.round(Math.min(wanted, fitWidth / ratio)) : wanted;
  if (!svg) return <LogoImg src={src} alt={alt} title={title} className={className} style={{ height }} />;
  return (
    <span
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      title={title}
      className={`logo-mask logo-art${className ? ` ${className}` : ''}`}
      style={{ height, width: height * ratio, WebkitMaskImage: `url("${src}")`, maskImage: `url("${src}")` }}
    />
  );
}
